import { z } from "zod";
import { type ApiRequestOptions, apiClient, authHeader } from "./api";
import { sourceKindSchema } from "./sources";

const baseUrl = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export const FeedbackCategory = {
  Issue: "issue",
  Proposal: "proposal",
  Other: "other",
} as const;
export type FeedbackCategory = (typeof FeedbackCategory)[keyof typeof FeedbackCategory];

export const FeedbackStatus = {
  Pending: "pending",
  InReview: "in_review",
  InProgress: "in_progress",
  Resolved: "resolved",
  Dismissed: "dismissed",
} as const;
export type FeedbackStatus = (typeof FeedbackStatus)[keyof typeof FeedbackStatus];

export const FeedbackAiStatus = {
  Pending: "pending",
  Succeeded: "succeeded",
  Failed: "failed",
} as const;
export type FeedbackAiStatus = (typeof FeedbackAiStatus)[keyof typeof FeedbackAiStatus];

export const FeedbackSource = {
  InApp: "in_app",
  Chat: "chat",
  Shortcut: "shortcut",
  Jira: "jira",
  Plugin: "plugin",
} as const;
export type FeedbackSource = (typeof FeedbackSource)[keyof typeof FeedbackSource];

export const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
  [FeedbackCategory.Issue]: "issue",
  [FeedbackCategory.Proposal]: "propuesta",
  [FeedbackCategory.Other]: "otro",
};

export const STATUS_LABELS: Record<FeedbackStatus, string> = {
  [FeedbackStatus.Pending]: "pendiente",
  [FeedbackStatus.InReview]: "en revisión",
  [FeedbackStatus.InProgress]: "en proceso",
  [FeedbackStatus.Resolved]: "resuelto",
  [FeedbackStatus.Dismissed]: "descartado",
};

export const SOURCE_LABELS: Record<FeedbackSource, string> = {
  [FeedbackSource.InApp]: "en la app",
  [FeedbackSource.Chat]: "chat",
  [FeedbackSource.Shortcut]: "Shortcut",
  [FeedbackSource.Jira]: "Jira",
  [FeedbackSource.Plugin]: "Plugin (MCP)",
};

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const feedbackCategorySchema = z.enum(FeedbackCategory);
const feedbackStatusSchema = z.enum(FeedbackStatus);
const feedbackAiStatusSchema = z.enum(FeedbackAiStatus);
export const feedbackSourceSchema = z.enum(FeedbackSource);
export const feedbackKnowledgeAnalysisStatusSchema = z.enum([
  "pending",
  "running",
  "succeeded",
  "failed",
]);
export const feedbackKnowledgeVerdictSchema = z.enum([
  "supported",
  "not_supported",
  "inconclusive",
]);
export const feedbackShortcutAssessmentSchema = z.enum([
  "concordant",
  "mismatch",
  "improvement",
  "mismatch_and_improvement",
  "inconclusive",
]);
export const feedbackShortcutObservationStateSchema = z.enum([
  "active",
  "paused_by_admin",
  "deleted_tombstone",
]);
export type FeedbackShortcutAssessment = z.infer<typeof feedbackShortcutAssessmentSchema>;
export type FeedbackShortcutObservationState = z.infer<
  typeof feedbackShortcutObservationStateSchema
>;

export const SHORTCUT_ASSESSMENT_LABELS: Record<FeedbackShortcutAssessment, string> = {
  concordant: "concuerda",
  mismatch: "no concuerda",
  improvement: "mejora posible",
  mismatch_and_improvement: "diferencia y mejora",
  inconclusive: "inconcluso",
};

export const SHORTCUT_OBSERVATION_LABELS: Record<FeedbackShortcutObservationState, string> = {
  active: "observación activa",
  paused_by_admin: "observación pausada",
  deleted_tombstone: "observación eliminada",
};

/**
 * Human labels for a Shortcut evaluation's `stable_reason` failure code,
 * following the shape and fallback-to-raw-code pattern of `REASON_LABELS` in
 * `frontend/src/components/feedback/resolution-card.tsx`. Any code not
 * covered here still renders — via {@link shortcutStableReasonLabel} — as
 * its raw snake_case value, so an admin always sees something, never a
 * blank field.
 */
export const SHORTCUT_STABLE_REASON_LABELS: Record<string, { label: string; hint: string }> = {
  triage_original_evidence_required: {
    label: "un hallazgo no citó el triage original",
    hint: "el modelo omitió la referencia al triage original en un hallazgo; el runtime actual la adjunta automáticamente y lo registra como reparación",
  },
  triage_solution_evidence_required: {
    label: "un hallazgo no citó prueba de la solución",
    hint: "ningún PR mergeado, diff ni resolución técnica respalda ese hallazgo",
  },
  triage_finding_family_invalid: {
    label: "hallazgos incoherentes con el veredicto",
    hint: "los ids mismatch-/improvement- de los hallazgos no coinciden con el assessment devuelto",
  },
  triage_evidence_reference_unknown: {
    label: "referencia de evidencia desconocida",
    hint: "un hallazgo cita una referencia de evidencia que no existe",
  },
  shortcut_context_pull_request_limit_exceeded: {
    label: "demasiados PRs enlazados",
    hint: "la historia enlaza más PRs que el tope del contexto de evaluación; no se llamó al modelo",
  },
  shortcut_context_feature_pull_request_limit_exceeded: {
    label: "demasiados PRs de feature para inspeccionar",
    hint: "más PRs de feature mergeados que el tope de diffs inspeccionables; no se llamó al modelo",
  },
  github_installation_unavailable: {
    label: "instalación de GitHub no disponible",
    hint: "no se pudo acceder a la instalación de GitHub necesaria para reunir evidencia",
  },
  triage_assessment_metric_invalid: {
    label: "métrica de assessment inválida",
    hint: "el modelo devolvió un assessment ausente o fuera de las categorías permitidas",
  },
  triage_assessment_reason_invalid: {
    label: "falta el motivo del veredicto inconcluso",
    hint: "el veredicto fue inconcluso pero el modelo no dio un motivo válido",
  },
  triage_context_evidence_missing: {
    label: "contexto de evaluación sin evidencia",
    hint: "el contexto vinculado a la ejecución no traía evidencia para respaldar un veredicto",
  },
  triage_context_incomplete: {
    label: "contexto de evaluación incompleto",
    hint: "el contexto vinculado a la ejecución quedó incompleto antes de correr el modelo",
  },
  triage_context_status_missing: {
    label: "falta el estado del contexto",
    hint: "el contexto vinculado a la ejecución no reportó su context_status",
  },
  triage_evidence_ref_limit_exceeded: {
    label: "demasiadas referencias de evidencia en un hallazgo",
    hint: "un hallazgo citó más de 10 referencias de evidencia; el modelo excedió el tope permitido",
  },
  triage_execution_partial: {
    label: "la ejecución del agente no terminó",
    hint: "el run del agente quedó parcial: se cortó antes de completar la evaluación",
  },
  triage_finding_limit_exceeded: {
    label: "demasiados hallazgos",
    hint: "el modelo devolvió más de 20 hallazgos; excede el tope permitido",
  },
  triage_findings_shape_invalid: {
    label: "hallazgos con forma inválida",
    hint: "el modelo devolvió hallazgos que no calzan con el esquema esperado",
  },
  enqueue_failed: {
    label: "no se pudo encolar la evaluación",
    hint: "el job de evaluación no logró encolarse",
  },
  execution_failed: {
    label: "falla de ejecución",
    hint: "error genérico en tiempo de ejecución durante la evaluación",
  },
  trigger_unavailable: {
    label: "disparador de Shortcut no disponible",
    hint: "el trigger o webhook de Shortcut no estaba disponible para iniciar la evaluación",
  },
  shortcut_context_expired: {
    label: "el contexto vinculado expiró",
    hint: "el contexto vinculado a la historia expiró antes de poder evaluarlo",
  },
};

/** `SHORTCUT_STABLE_REASON_LABELS[code]`, falling back to the raw code with no hint. */
export function shortcutStableReasonLabel(code: string): { label: string; hint: string } {
  return SHORTCUT_STABLE_REASON_LABELS[code] ?? { label: code, hint: "" };
}

export const feedbackJiraAssessmentSchema = z.enum([
  "concordant",
  "mismatch",
  "improvement",
  "mismatch_and_improvement",
  "inconclusive",
]);
export const feedbackJiraObservationStateSchema = z.enum([
  "active",
  "paused_by_admin",
  "deleted_tombstone",
]);
export type FeedbackJiraAssessment = z.infer<typeof feedbackJiraAssessmentSchema>;
export type FeedbackJiraObservationState = z.infer<typeof feedbackJiraObservationStateSchema>;

export const JIRA_ASSESSMENT_LABELS: Record<FeedbackJiraAssessment, string> = {
  concordant: "concuerda",
  mismatch: "no concuerda",
  improvement: "mejora posible",
  mismatch_and_improvement: "diferencia y mejora",
  inconclusive: "inconcluso",
};

export const JIRA_OBSERVATION_LABELS: Record<FeedbackJiraObservationState, string> = {
  active: "observación activa",
  paused_by_admin: "observación pausada",
  deleted_tombstone: "observación eliminada",
};

/**
 * Why a Jira note's observation is not active. The wire type is a plain string
 * (the backend may grow reasons), so an unknown code renders as itself.
 */
export const JIRA_SUSPENSION_REASON_LABELS: Record<string, string> = {
  admin_request: "pausada por un administrador",
  generation_limit: "se alcanzó el tope de evaluaciones",
  feedback_report_deleted: "el reporte fue eliminado",
};

/** `JIRA_SUSPENSION_REASON_LABELS[code]`, falling back to the raw code. */
export function jiraSuspensionReasonLabel(code: string): string {
  return JIRA_SUSPENSION_REASON_LABELS[code] ?? code;
}

/**
 * Human labels for a Jira note evaluation's `stable_reason`: the backend's
 * preflight reasons (no model call), runtime failures and the semantic contract
 * codes. Two things share the column: these stable codes and, for an `inconclusive`
 * verdict, a model-authored reason of up to 1,000 characters of free text.
 * {@link jiraStableReasonLabel} therefore returns any string it does not know
 * unchanged, and callers render it as plain text (never as markup).
 */
export const JIRA_STABLE_REASON_LABELS: Record<string, { label: string; hint: string }> = {
  // Preflight: the backend ended the run before any model call.
  jira_not_configured: {
    label: "Jira no está configurado",
    hint: "no hay una conexión de Jira activa para el workspace de la nota; no se llamó al modelo",
  },
  jira_project_not_enabled: {
    label: "proyecto de Jira no habilitado",
    hint: "el proyecto del ticket no está habilitado en la integración; no se llamó al modelo",
  },
  jira_issue_not_done: {
    label: "el ticket ya no está resuelto",
    hint: "al releer el ticket su categoría de estado no era Done; no se leyó ningún comentario",
  },
  no_resolution_evidence: {
    label: "sin evidencia de resolución",
    hint: "el ticket no tiene comentarios humanos posteriores a la nota; no se llamó al modelo",
  },
  jira_context_over_limit: {
    label: "el contexto del ticket excede el límite",
    hint: "el ticket supera los 100 comentarios humanos o los 128 KiB de texto; no se llamó al modelo",
  },
  jira_context_invalid: {
    label: "contexto del ticket inválido",
    hint: "el ticket releído no corresponde a la nota (otra clave) o tiene comentarios sin un id ordenable",
  },
  unchanged_since_last_evaluation: {
    label: "sin cambios desde la última evaluación",
    hint: "el ticket no cambió desde la última evaluación completa; no se llamó al modelo",
  },
  jira_target_not_active: {
    label: "la observación se pausó o se eliminó antes de evaluar",
    hint: "un administrador pausó o eliminó la observación después de admitir esta evaluación; no se leyó el ticket ni se llamó al modelo",
  },
  jira_context_retry_snapshot_unavailable: {
    label: "no hay instantánea del contexto para reintentar",
    hint: "el reintento no encontró la instantánea del ticket que se había guardado",
  },
  // Failures: the run stopped without a valid result.
  jira_context_provider_failed: {
    label: "no se pudo leer el ticket en Jira",
    hint: "Jira rechazó o no pudo atender la lectura del ticket; no se llamó al modelo",
  },
  jira_context_provider_transient: {
    label: "Jira no respondió",
    hint: "error transitorio de Jira (5xx, límite de tasa o conexión) durante la lectura del ticket",
  },
  jira_context_binding_missing: {
    label: "falta el vínculo con la nota",
    hint: "la ejecución no quedó ligada a una nota evaluada ni a su workspace de Jira",
  },
  jira_context_budget_invalid: {
    label: "presupuesto del agente fuera de los topes",
    hint: "el agente excede 2 turnos, 5 llamadas a herramientas, 512 KiB de evidencia o USD 0.50",
  },
  jira_context_encryption_unavailable: {
    label: "cifrado de credenciales no disponible",
    hint: "falta la clave para descifrar las credenciales de Jira; no se pudo leer el ticket",
  },
  jira_context_arguments_forbidden: {
    label: "el modelo envió argumentos a la herramienta de contexto",
    hint: "la herramienta no acepta argumentos: el ticket lo fija el servidor, nunca el modelo",
  },
  jira_context_already_issued: {
    label: "el contexto ya se había entregado",
    hint: "el contexto se entrega una sola vez por ejecución y el modelo lo pidió de nuevo",
  },
  jira_evidence_kind_invalid: {
    label: "tipo de evidencia inválido",
    hint: "una fila de evidencia no es de los tres tipos permitidos (nota, estado final, comentario)",
  },
  jira_evidence_reference_duplicate: {
    label: "referencia de evidencia duplicada",
    hint: "dos filas de evidencia comparten la misma referencia",
  },
  jira_evidence_reference_foreign: {
    label: "referencia de evidencia ajena a la ejecución",
    hint: "una referencia de evidencia no pertenece a esta ejecución",
  },
  jira_evidence_resource_missing: {
    label: "evidencia sin recurso",
    hint: "una fila de evidencia no trae el recurso al que apunta",
  },
  retry_exhausted: {
    label: "reintentos agotados",
    hint: "la ejecución agotó sus reintentos sin producir un resultado",
  },
  execution_failed: {
    label: "falla de ejecución",
    hint: "error genérico en tiempo de ejecución durante la evaluación",
  },
  enqueue_failed: {
    label: "no se pudo encolar la evaluación",
    hint: "el job de evaluación no logró encolarse",
  },
  execution_result_missing: {
    label: "la ejecución terminó sin resultado",
    hint: "la ejecución se marcó como exitosa pero no quedó su resultado guardado",
  },
  // Semantic contract: the model's result did not hold up.
  note_assessment_metric_invalid: {
    label: "métrica de assessment inválida",
    hint: "el modelo devolvió un assessment ausente, repetido o fuera de las categorías permitidas",
  },
  note_assessment_reason_invalid: {
    label: "falta el motivo del veredicto inconcluso",
    hint: "el veredicto fue inconcluso pero el modelo no dio un motivo válido (hasta 1.000 caracteres)",
  },
  note_context_evidence_missing: {
    label: "contexto de evaluación sin evidencia",
    hint: "el contexto vinculado a la ejecución no traía la evidencia necesaria para un veredicto",
  },
  note_context_incomplete: {
    label: "contexto de evaluación incompleto",
    hint: "el contexto del ticket quedó incompleto antes de correr el modelo",
  },
  note_context_status_missing: {
    label: "falta el estado del contexto",
    hint: "el contexto vinculado a la ejecución no reportó su estado",
  },
  note_evidence_ref_limit_exceeded: {
    label: "demasiadas referencias de evidencia en un hallazgo",
    hint: "un hallazgo citó más de 10 referencias de evidencia",
  },
  note_evidence_reference_unknown: {
    label: "referencia de evidencia desconocida",
    hint: "un hallazgo cita una referencia que no existe en el contexto entregado",
  },
  note_execution_partial: {
    label: "la ejecución del agente no terminó",
    hint: "el run quedó parcial: se cortó antes de completar la evaluación",
  },
  note_finding_family_invalid: {
    label: "hallazgos incoherentes con el veredicto",
    hint: "los ids mismatch-/improvement- de los hallazgos no coinciden con el assessment devuelto",
  },
  note_finding_limit_exceeded: {
    label: "demasiados hallazgos",
    hint: "el modelo devolvió más de 10 hallazgos",
  },
  note_findings_shape_invalid: {
    label: "hallazgos con forma inválida",
    hint: "el modelo devolvió hallazgos que no calzan con el esquema esperado",
  },
  note_original_evidence_required: {
    label: "un hallazgo no citó la nota original",
    hint: "todo hallazgo debe citar la nota original; el runtime la adjunta cuando falta y lo registra como reparación",
  },
  note_solution_evidence_required: {
    label: "un hallazgo no citó prueba de la resolución",
    hint: "todo hallazgo debe citar el estado final o un comentario de resolución que lo respalde",
  },
};

/** `JIRA_STABLE_REASON_LABELS[code]`, falling back to the text itself with no hint. */
export function jiraStableReasonLabel(code: string): { label: string; hint: string } {
  return JIRA_STABLE_REASON_LABELS[code] ?? { label: code, hint: "" };
}

/** Knowledge-resolution lifecycle (resolve-feedback-with-knowledge-proposals). */
export const FeedbackResolutionState = {
  Queued: "queued",
  Running: "running",
  KnowledgeProposed: "knowledge_proposed",
  KnowledgeProcessing: "knowledge_processing",
  ResolvedByKnowledge: "resolved_by_knowledge",
  NeedsClaude: "needs_claude",
  Failed: "failed",
} as const;
export type FeedbackResolutionState =
  (typeof FeedbackResolutionState)[keyof typeof FeedbackResolutionState];

export const feedbackResolutionStateSchema = z.enum(FeedbackResolutionState);

export const RESOLUTION_STATE_LABELS: Record<FeedbackResolutionState, string> = {
  [FeedbackResolutionState.Queued]: "análisis en cola",
  [FeedbackResolutionState.Running]: "analizando",
  [FeedbackResolutionState.KnowledgeProposed]: "propuesta pendiente de revisión",
  [FeedbackResolutionState.KnowledgeProcessing]: "conocimiento aprobado, procesando",
  [FeedbackResolutionState.ResolvedByKnowledge]: "resuelto por conocimiento",
  [FeedbackResolutionState.NeedsClaude]: "requiere Claude",
  [FeedbackResolutionState.Failed]: "análisis fallido",
};

export const resolutionDestinationSchema = z.enum(["validator", "sources", "claude", "none"]);
export type ResolutionDestination = z.infer<typeof resolutionDestinationSchema>;

export const feedbackResolutionLinkSchema = z
  .object({
    original_knowledge_item_id: z.guid().nullable(),
    live_knowledge_item_id: z.guid().nullable(),
    link_kind: z.enum(["created", "canonical_duplicate"]).nullable(),
  })
  .strict();
export type FeedbackResolutionLink = z.infer<typeof feedbackResolutionLinkSchema>;

export const feedbackResolutionBadgeSchema = z
  .object({
    state: feedbackResolutionStateSchema,
    retry_allowed: z.boolean(),
  })
  .strict();
export type FeedbackResolutionBadge = z.infer<typeof feedbackResolutionBadgeSchema>;

export const feedbackResolutionDetailSchema = z
  .object({
    state: feedbackResolutionStateSchema,
    generation: z.number().int().positive(),
    reason_code: z.string().nullable(),
    summary: z.string().nullable(),
    rationale: z.string().nullable(),
    confidence: z.number().nullable(),
    proposed_title: z.string().nullable(),
    validation_status: z.enum(["pending", "approved", "rejected"]).nullable(),
    assessment_status: z.enum(["queued", "running", "completed", "failed"]).nullable(),
    assessment_relevance: z.enum(["high", "medium", "low"]).nullable(),
    assessment_summary: z.string().nullable(),
    processing_state: z.enum(["pending", "running", "done", "failed"]).nullable(),
    processing_error: z.string().nullable(),
    kr_id: z.guid().nullable(),
    retry_allowed: z.boolean(),
    destination: resolutionDestinationSchema,
    link: feedbackResolutionLinkSchema,
  })
  .strict();
export type FeedbackResolutionDetail = z.infer<typeof feedbackResolutionDetailSchema>;

export const submitFeedbackResponseSchema = z.object({
  id: z.guid(),
  category: feedbackCategorySchema,
  status: z.literal("pending"),
  ai_status: z.literal("pending"),
  created_at: z.string(),
});
export type SubmitFeedbackResponse = z.infer<typeof submitFeedbackResponseSchema>;

export const ownFeedbackItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  category: feedbackCategorySchema,
  status: feedbackStatusSchema,
  ai_status: feedbackAiStatusSchema,
  knowledge_analysis_status: feedbackKnowledgeAnalysisStatusSchema.nullable().optional(),
  knowledge_verdict: feedbackKnowledgeVerdictSchema.nullable().optional(),
  created_at: z.string(),
});
export type OwnFeedbackItem = z.infer<typeof ownFeedbackItemSchema>;

export const ownFeedbackListSchema = z.object({
  items: z.array(ownFeedbackItemSchema),
  total: z.number(),
});
export type OwnFeedbackList = z.infer<typeof ownFeedbackListSchema>;

export const adminFeedbackItemSchema = z.object({
  id: z.string(),
  source: feedbackSourceSchema,
  user_id: z.string().nullable(),
  user_email: z.string().nullable(),
  category: feedbackCategorySchema,
  ai_suggested_category: feedbackCategorySchema.nullable(),
  status: feedbackStatusSchema,
  ai_status: feedbackAiStatusSchema,
  title: z.string(),
  created_at: z.string(),
  archived_at: z.string().nullable(),
  shortcut_story_public_id: z.number().int().positive().nullable().optional(),
  shortcut_story_name: z.string().nullable().optional(),
  shortcut_latest_assessment: feedbackShortcutAssessmentSchema.nullable().optional(),
  shortcut_observation_state: feedbackShortcutObservationStateSchema.nullable().optional(),
  shortcut_new_evaluation_available: z.boolean().optional(),
  jira_issue_key: z.string().nullable().optional(),
  jira_latest_assessment: feedbackJiraAssessmentSchema.nullable().optional(),
  jira_observation_state: feedbackJiraObservationStateSchema.nullable().optional(),
  jira_new_evaluation_available: z.boolean().optional(),
  resolution: feedbackResolutionBadgeSchema.nullable().optional(),
});
export type AdminFeedbackItem = z.infer<typeof adminFeedbackItemSchema>;

export const adminFeedbackListSchema = z.object({
  items: z.array(adminFeedbackItemSchema),
  total: z.number(),
});
export type AdminFeedbackList = z.infer<typeof adminFeedbackListSchema>;

export const feedbackStatsSchema = z.object({
  pending_count: z.number().int().nonnegative(),
  active_count: z.number().int().nonnegative(),
});
export type FeedbackStats = z.infer<typeof feedbackStatsSchema>;

export const feedbackAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size_bytes: z.number(),
  kind: z.enum(["image", "video"]),
});
export type FeedbackAttachment = z.infer<typeof feedbackAttachmentSchema>;

export const feedbackPluginAreaSchema = z.enum(["knowledge", "rag_tool", "plugin"]);
export type FeedbackPluginArea = z.infer<typeof feedbackPluginAreaSchema>;

export const PLUGIN_AREA_LABELS: Record<FeedbackPluginArea, string> = {
  knowledge: "Conocimiento",
  rag_tool: "Tools del RAG",
  plugin: "Plugin",
};

// Optional fields tolerate both `null` and a missing key: the backend may
// serialize either. Deliberately not `.strict()` so a field added to the
// contract later does not blank the whole report.
export const feedbackPluginToolCallSchema = z.object({
  tool: z.string(),
  outcome: z.enum(["ok", "error"]),
  arguments_excerpt: z.string().nullish(),
  result_excerpt: z.string().nullish(),
});
export type FeedbackPluginToolCall = z.infer<typeof feedbackPluginToolCallSchema>;

export const feedbackPluginContextSchema = z.object({
  area: feedbackPluginAreaSchema,
  session_summary: z.string(),
  tool_calls: z.array(feedbackPluginToolCallSchema),
  client: z
    .object({
      name: z.string().nullish(),
      plugin_version: z.string().nullish(),
      model: z.string().nullish(),
    })
    .nullish(),
  sdd: z
    .object({
      ticket: z.string().nullish(),
      phase: z.string().nullish(),
    })
    .nullish(),
  beflow_session_id: z.string().nullish(),
  beflow_turn_id: z.string().nullish(),
});
export type FeedbackPluginContext = z.infer<typeof feedbackPluginContextSchema>;

export const feedbackContextSchema = z
  .object({
    route: z.string().optional(),
    user_agent: z.string().optional(),
    viewport: z.string().optional(),
  })
  .nullable();
export type FeedbackContext = z.infer<typeof feedbackContextSchema>;

export const feedbackKnowledgeEvidenceSchema = z.object({
  knowledge_item_id: z.guid().nullable(),
  rank: z.number().int().min(1).max(5),
  source_type: sourceKindSchema,
  display_title: z.string(),
  source_updated_at: z.string(),
  excerpt: z.string(),
  rationale: z.string(),
  confidence: z.number().min(0).max(1),
  source_url: z.string().nullable(),
});

export const feedbackKnowledgeDetailSchema = z.object({
  issue_type: z.enum(["incorrect", "outdated", "contradictory"]),
  chat_message_id: z.guid().nullable(),
  chat_session_id: z.guid(),
  question: z.string().nullable(),
  answer: z.string(),
  citations: z.array(z.record(z.string(), z.unknown())),
  analysis_status: feedbackKnowledgeAnalysisStatusSchema,
  analysis_generation: z.number().int().positive(),
  verdict: feedbackKnowledgeVerdictSchema.nullable(),
  summary: z.string().nullable(),
  search_queries: z.array(z.string()),
  cited_candidate_count: z.number().int().nonnegative(),
  retrieved_candidate_count: z.number().int().nonnegative(),
  candidate_count: z.number().int().min(0).max(12),
  degraded_legs: z.array(z.string()),
  failure_code: z.string().nullable(),
  retry_allowed: z.boolean(),
  evidence: z.array(feedbackKnowledgeEvidenceSchema).max(5),
});
export type FeedbackKnowledgeDetail = z.infer<typeof feedbackKnowledgeDetailSchema>;

const feedbackShortcutFindingSchema = z
  .object({
    finding_id: z.string(),
    severity: z.string(),
    title: z.string(),
    summary: z.string(),
    evidence_refs: z.array(z.string()),
  })
  .strict();

const feedbackShortcutEvidenceSchema = z
  .object({
    reference_id: z.string(),
    source_kind: z.string(),
    resource_id: z.union([z.string(), z.number()]).nullable(),
    display_title: z.string(),
    content_hash: z.string(),
    size_bytes: z.number().int().nonnegative(),
  })
  .strict();

// Whether the fix reached production, plus its production PR reference(s)
// and latest merge time. Written directly into `story_snapshot` by the
// backend's reconciliation persistence path, so it is optional/nullable:
// older records (evaluated before this field existed) never carry it, and
// it stays `null` until the first reconciliation pass observes a merge.
const feedbackShortcutProductionReachSchema = z
  .object({
    reached: z.boolean(),
    pull_requests: z.array(z.object({ repo: z.string(), number: z.number().int() }).strict()),
    latest_merged_at: z.string().nullable(),
  })
  .strict();
export type FeedbackShortcutProductionReach = z.infer<typeof feedbackShortcutProductionReachSchema>;

export const feedbackShortcutPullRequestStageSchema = z.enum([
  "feature",
  "promotion",
  "production",
  "unmerged",
]);
export type FeedbackShortcutPullRequestStage = z.infer<
  typeof feedbackShortcutPullRequestStageSchema
>;

// Sanitized projection of `ShortcutPullRequestObservation` (backend
// `evaluation_context.py`): `_shortcut_detail` narrows the raw row to these
// fields before it reaches the API, dropping `merge_commit_sha`/`base_sha`/
// `head_sha`/`head_ref` — internal git identifiers with no admin-facing
// purpose. The key itself may be absent on older story snapshots that
// predate this field, hence the default. Rows persisted before the stage
// classifier (2026-09-09) carry only `repo`/`number`/`state`/`merged`/
// `merged_at`, so everything but the identity is optional: a strict schema
// that required `stage` rejected the whole report for sc-69025 in prod.
const feedbackShortcutPullRequestObservationSchema = z
  .object({
    repo: z.string(),
    number: z.number().int(),
    state: z.string().optional(),
    merged: z.boolean().optional(),
    merged_at: z.string().nullable().optional(),
    base_ref: z.string().nullable().optional(),
    stage: feedbackShortcutPullRequestStageSchema.optional(),
  })
  .strict();
export type FeedbackShortcutPullRequestObservation = z.infer<
  typeof feedbackShortcutPullRequestObservationSchema
>;

export const feedbackShortcutStorySchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    name: z.string().optional(),
    description: z.string().optional(),
    workflow_state_id: z.union([z.string(), z.number()]).optional(),
    workflow_state_name: z.string().nullable().optional(),
    completed_at: z.string().nullable().optional(),
    app_url: z.string().optional(),
    branches: z.array(z.string()).optional(),
    pull_requests: z
      .array(z.object({ number: z.number().int(), url: z.string() }).strict())
      .optional(),
    repository: z.string().optional(),
    base_ref: z.string().optional(),
    base_commit_sha: z.string().optional(),
    production_reach: feedbackShortcutProductionReachSchema.nullable().optional(),
    pull_request_observations: z.array(feedbackShortcutPullRequestObservationSchema).default([]),
  })
  .strict();

export const feedbackShortcutThreadRowSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    parent_id: z.union([z.string(), z.number()]).nullable().optional(),
    author_id: z.union([z.string(), z.number()]).nullable().optional(),
    text: z.string().optional(),
    deleted: z.boolean().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .strict();
export type FeedbackShortcutThreadRow = z.infer<typeof feedbackShortcutThreadRowSchema>;

export const feedbackShortcutParticipantSchema = z
  .object({
    id: z.union([z.string(), z.number()]),
    name: z.string(),
    mention_name: z.string(),
  })
  .strict();
export type FeedbackShortcutParticipant = z.infer<typeof feedbackShortcutParticipantSchema>;

export const feedbackShortcutReactionSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    comment_id: z.union([z.string(), z.number()]),
    actor_id: z.union([z.string(), z.number()]).optional(),
    emoji: z.string().optional(),
    family: z.string(),
  })
  .strict();
export type FeedbackShortcutReaction = z.infer<typeof feedbackShortcutReactionSchema>;

export const feedbackShortcutGenerationDetailSchema = z
  .object({
    id: z.guid(),
    generation: z.number().int().positive(),
    cause: z.enum(["completion", "thread_changed"]),
    execution_id: z.guid().nullable(),
    agent_id: z.guid().nullable(),
    agent_version_id: z.guid().nullable(),
    context_status: z.enum(["pending", "complete", "inconclusive", "expired"]),
    status: z.enum(["pending", "running", "succeeded", "failed"]),
    assessment: feedbackShortcutAssessmentSchema.nullable(),
    summary: z.string().nullable(),
    findings: z.array(feedbackShortcutFindingSchema),
    evidence: z.array(feedbackShortcutEvidenceSchema),
    story: feedbackShortcutStorySchema.nullable(),
    thread: z.array(feedbackShortcutThreadRowSchema).nullable(),
    participants: z.array(feedbackShortcutParticipantSchema).nullable(),
    reactions: z.array(feedbackShortcutReactionSchema).nullable(),
    stable_reason: z.string().nullable(),
    content_expires_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    content_expired: z.boolean(),
    warnings: z.array(z.string()),
  })
  .strict();
export type FeedbackShortcutGenerationDetail = z.infer<
  typeof feedbackShortcutGenerationDetailSchema
>;

export const feedbackShortcutDetailSchema = z
  .object({
    target_id: z.guid(),
    story_public_id: z.number().int().positive(),
    story_name: z.string().nullable(),
    story_url: z.string().nullable(),
    triage_comment_id: z.string(),
    original_triage_plan: z.record(z.string(), z.unknown()),
    original_triage_comment: z.string(),
    observation_state: feedbackShortcutObservationStateSchema,
    suspension_reason: z.string().nullable(),
    latest_assessment: feedbackShortcutAssessmentSchema.nullable(),
    new_evaluation_available: z.boolean(),
    deletion_tombstone: z.boolean(),
    generations: z.array(feedbackShortcutGenerationDetailSchema),
  })
  .strict();
export type FeedbackShortcutDetail = z.infer<typeof feedbackShortcutDetailSchema>;

// Rows of the Jira audit are untyped JSON on the backend; its allowlists
// (`JIRA_*_KEYS` in `backend/src/api/feedback.py`) are the contract these strict
// schemas mirror, so an author, e-mail or reporter can never be declared here.
const feedbackJiraFindingSchema = z
  .object({
    finding_id: z.string(),
    severity: z.string(),
    title: z.string(),
    summary: z.string(),
    evidence_refs: z.array(z.string()),
  })
  .strict();

const feedbackJiraEvidenceSchema = z
  .object({
    reference_id: z.string(),
    source_kind: z.string(),
    resource_id: z.string().nullable(),
    display_title: z.string(),
    content_hash: z.string(),
    size_bytes: z.number().int().nonnegative(),
  })
  .strict();

const feedbackJiraFinalStateSchema = z
  .object({
    key: z.string(),
    summary: z.string(),
    status_name: z.string().nullable(),
    status_category_key: z.string(),
    issue_type: z.string().nullable(),
    labels: z.array(z.string()),
    description: z.string(),
    note_comment_present: z.boolean(),
  })
  .strict();

export const feedbackJiraThreadVisibilitySchema = z.enum(["internal", "public"]);
export const feedbackJiraThreadPositionSchema = z.enum(["before_note", "after_note"]);

const feedbackJiraThreadRowSchema = z
  .object({
    id: z.string(),
    visibility: feedbackJiraThreadVisibilitySchema,
    position: feedbackJiraThreadPositionSchema,
    text: z.string(),
  })
  .strict();
export type FeedbackJiraThreadRow = z.infer<typeof feedbackJiraThreadRowSchema>;

export const feedbackJiraGenerationDetailSchema = z
  .object({
    id: z.guid(),
    generation: z.number().int().positive(),
    execution_id: z.guid().nullable(),
    agent_id: z.guid().nullable(),
    agent_version_id: z.guid().nullable(),
    provider: z.string().nullable(),
    model: z.string().nullable(),
    context_status: z.enum(["pending", "complete", "inconclusive"]),
    status: z.enum(["pending", "running", "succeeded", "failed"]),
    assessment: feedbackJiraAssessmentSchema.nullable(),
    summary: z.string().nullable(),
    findings: z.array(feedbackJiraFindingSchema),
    evidence: z.array(feedbackJiraEvidenceSchema),
    warnings: z.array(z.string()),
    final_state: feedbackJiraFinalStateSchema.nullable(),
    thread: z.array(feedbackJiraThreadRowSchema).nullable(),
    stable_reason: z.string().nullable(),
    content_expires_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    content_expired: z.boolean(),
  })
  .strict();
export type FeedbackJiraGenerationDetail = z.infer<typeof feedbackJiraGenerationDetailSchema>;

export const feedbackJiraDetailSchema = z
  .object({
    target_id: z.guid(),
    issue_key: z.string(),
    issue_url: z.string().nullable(),
    note_comment_id: z.string(),
    original_note: z.record(z.string(), z.unknown()),
    original_note_rendering: z.string(),
    note_provider: z.string().nullable(),
    note_model: z.string().nullable(),
    posted_at: z.string(),
    observation_state: feedbackJiraObservationStateSchema,
    suspension_reason: z.string().nullable(),
    latest_assessment: feedbackJiraAssessmentSchema.nullable(),
    new_evaluation_available: z.boolean(),
    deletion_tombstone: z.boolean(),
    generations: z.array(feedbackJiraGenerationDetailSchema),
  })
  .strict();
export type FeedbackJiraDetail = z.infer<typeof feedbackJiraDetailSchema>;

export const feedbackDetailSchema = z.object({
  id: z.string(),
  source: feedbackSourceSchema,
  user_id: z.string().nullable(),
  user_email: z.string().nullable(),
  category: feedbackCategorySchema,
  ai_suggested_category: feedbackCategorySchema.nullable(),
  status: feedbackStatusSchema,
  ai_status: feedbackAiStatusSchema,
  ai_title: z.string().nullable(),
  ai_detail: z.string().nullable(),
  ai_provider: z.string().nullable().optional(),
  ai_model: z.string().nullable().optional(),
  ai_served_via_fallback: z.boolean().nullable().optional(),
  description: z.string(),
  context: feedbackContextSchema,
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  attachments: z.array(feedbackAttachmentSchema),
  knowledge: feedbackKnowledgeDetailSchema.nullable().optional(),
  shortcut: feedbackShortcutDetailSchema.nullable().optional(),
  jira: feedbackJiraDetailSchema.nullable().optional(),
  resolution: feedbackResolutionDetailSchema.nullable().optional(),
  plugin_context: feedbackPluginContextSchema.nullable().optional(),
});
export type FeedbackDetail = z.infer<typeof feedbackDetailSchema>;

export const feedbackResolutionRetrySchema = z
  .object({
    report_id: z.guid(),
    generation: z.number().int().positive(),
    state: feedbackResolutionStateSchema,
  })
  .strict();
export type FeedbackResolutionRetry = z.infer<typeof feedbackResolutionRetrySchema>;

export const attachmentUrlSchema = z.object({
  url: z.string(),
  expires_in: z.number(),
});
export type AttachmentUrl = z.infer<typeof attachmentUrlSchema>;

export const feedbackKnowledgeRetrySchema = z.object({
  report_id: z.guid(),
  analysis_generation: z.number().int().positive(),
  analysis_status: z.enum(["pending", "failed"]),
});

// ---------------------------------------------------------------------------
// File validation constants
// ---------------------------------------------------------------------------

export const IMAGE_EXTENSIONS = [".png", ".jpg", ".jpeg", ".webp", ".gif"] as const;
export const VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm"] as const;
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024; // 10 MiB
export const VIDEO_MAX_BYTES = 200 * 1024 * 1024; // 200 MiB
export const MAX_FILE_COUNT = 5;

export type AttachmentKind = "image" | "video";

export function detectAttachmentKind(filename: string): AttachmentKind | null {
  const lower = filename.toLowerCase();
  if (IMAGE_EXTENSIONS.some((ext) => lower.endsWith(ext))) return "image";
  if (VIDEO_EXTENSIONS.some((ext) => lower.endsWith(ext))) return "video";
  return null;
}

export interface FileValidationError {
  filename: string;
  message: string;
}

export function validateFiles(files: File[]): FileValidationError[] {
  const errors: FileValidationError[] = [];
  if (files.length > MAX_FILE_COUNT) {
    errors.push({ filename: "", message: `máximo ${MAX_FILE_COUNT} archivos por reporte` });
    return errors;
  }
  for (const file of files) {
    const kind = detectAttachmentKind(file.name);
    if (!kind) {
      errors.push({
        filename: file.name,
        message: "tipo no permitido (se aceptan imágenes y videos)",
      });
      continue;
    }
    if (kind === "image" && file.size > IMAGE_MAX_BYTES) {
      errors.push({ filename: file.name, message: "imágenes: máximo 10 MiB" });
    }
    if (kind === "video" && file.size > VIDEO_MAX_BYTES) {
      errors.push({ filename: file.name, message: "videos: máximo 200 MiB" });
    }
  }
  return errors;
}

// ---------------------------------------------------------------------------
// API — multipart submit (mirrors upload.ts pattern)
// ---------------------------------------------------------------------------

export interface SubmitFeedbackParams {
  description: string;
  category: FeedbackCategory;
  context?: { route: string; user_agent: string; viewport: string };
  files: File[];
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/** The GitHub URL for a `repo` (`owner/name`) pull request `number`. */
export function githubPullRequestUrl(repo: string, number: number): string {
  return `https://github.com/${repo}/pull/${number}`;
}

/**
 * Parses a GitHub evidence `resource_id` (`github:<owner>/<repo>:pull:<n>`
 * or the `:diff` variant) into the pull request's GitHub URL. The `:diff`
 * suffix resolves to the PR's `/files` tab. Any other shape — including the
 * `:stage` variant, which is context, not proof — returns `null` so callers
 * never render a broken link.
 */
export function githubResourceUrl(resourceId: string): string | null {
  const match = /^github:([^/]+)\/([^:]+):pull:(\d+)(:diff)?$/.exec(resourceId);
  if (!match) return null;
  const [, owner, repo, number, diffSuffix] = match;
  const base = githubPullRequestUrl(`${owner}/${repo}`, Number(number));
  return diffSuffix ? `${base}/files` : base;
}

/**
 * Assigns each unique `reference_id` a stable 1-based index in first-seen
 * order, used to render `E1`, `E2`, ... chips that a finding's
 * `evidence_refs` can look up.
 */
export function indexEvidence(evidence: readonly { reference_id: string }[]): Map<string, number> {
  const index = new Map<string, number>();
  for (const item of evidence) {
    if (!index.has(item.reference_id)) {
      index.set(item.reference_id, index.size + 1);
    }
  }
  return index;
}

/**
 * The thread rows that are not the root triage comment, comparing `id` (as
 * a string) against `triageCommentId`. Callers derive both the rendered
 * reply list and its count from this, so neither ever includes the seed
 * comment that is present by construction.
 */
export function threadReplies<T extends { id: string | number }>(
  thread: readonly T[],
  triageCommentId: string,
): T[] {
  return thread.filter((row) => String(row.id) !== triageCommentId);
}

/** A parsed segment of a Shortcut comment body, from {@link tokenizeShortcutText}. */
export type ShortcutTextToken =
  | { kind: "text"; value: string }
  | { kind: "mention"; mentionName: string; memberId: string }
  | { kind: "code"; value: string };

/**
 * Matches Shortcut's `[@name](shortcutapp://members/<uuid>)` mention markup
 * (captures the mention name and member id) or an inline code span
 * `` `x` `` (captures its content). Any text between/around matches is
 * carried as plain `text` tokens by {@link tokenizeShortcutText} — an
 * unclosed `[@name` never matches, so it stays plain text.
 */
const SHORTCUT_TOKEN_PATTERN = /\[@([^\]]+)\]\(shortcutapp:\/\/members\/([^)]+)\)|`([^`]+)`/g;

/**
 * Splits a raw Shortcut comment body into typed tokens so the UI can render
 * mentions as chips and inline code as `<code>`, instead of the raw
 * `[@name](shortcutapp://members/<uuid>)` / `` `x` `` markup — never via
 * `dangerouslySetInnerHTML`. An empty string yields no tokens.
 */
export function tokenizeShortcutText(text: string): ShortcutTextToken[] {
  const tokens: ShortcutTextToken[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(SHORTCUT_TOKEN_PATTERN)) {
    const index = match.index ?? 0;
    if (index > lastIndex) {
      tokens.push({ kind: "text", value: text.slice(lastIndex, index) });
    }
    const [full, mentionName, memberId, codeValue] = match;
    if (mentionName !== undefined && memberId !== undefined) {
      tokens.push({ kind: "mention", mentionName, memberId });
    } else if (codeValue !== undefined) {
      tokens.push({ kind: "code", value: codeValue });
    }
    lastIndex = index + full.length;
  }
  if (lastIndex < text.length) {
    tokens.push({ kind: "text", value: text.slice(lastIndex) });
  }
  return tokens;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

async function submitFeedback(params: SubmitFeedbackParams): Promise<SubmitFeedbackResponse> {
  const formData = new FormData();
  formData.append("description", params.description);
  formData.append("category", params.category);
  if (params.context) {
    formData.append("context", JSON.stringify(params.context));
  }
  for (const file of params.files) {
    formData.append("files", file, file.name);
  }

  const headers: Record<string, string> = { Accept: "application/json", ...(await authHeader()) };
  const response = await fetch(`${baseUrl}/feedback`, {
    method: "POST",
    headers,
    body: formData,
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Submit failed (${response.status}): ${text || response.statusText}`);
  }
  const json: unknown = await response.json();
  return submitFeedbackResponseSchema.parse(json);
}

// ---------------------------------------------------------------------------
// API — JSON endpoints (via apiClient pattern)
// ---------------------------------------------------------------------------

export interface ListAdminFeedbackParams {
  limit?: number;
  offset?: number;
  q?: string;
  category?: FeedbackCategory;
  status?: readonly FeedbackStatus[];
  source?: FeedbackSource;
  user_id?: string;
  user_email?: string;
  archived?: boolean;
}

const VOID = z.unknown().transform(() => undefined);

export const feedbackApi = {
  submit: submitFeedback,

  mine: (params: { limit?: number; offset?: number } = {}) =>
    apiClient.get("/feedback/mine", ownFeedbackListSchema, {
      query: { limit: params.limit, offset: params.offset },
    }),

  adminList: (params: ListAdminFeedbackParams = {}) => {
    const query: NonNullable<ApiRequestOptions["query"]> = {};
    if (params.limit !== undefined) query.limit = params.limit;
    if (params.offset !== undefined) query.offset = params.offset;
    if (params.q && params.q.length > 0) query.q = params.q;
    if (params.category !== undefined) query.category = params.category;
    if (params.status !== undefined && params.status.length > 0) query.status = params.status;
    if (params.source !== undefined) query.source = params.source;
    if (params.user_email && params.user_email.length > 0) query.user_email = params.user_email;
    if (params.archived !== undefined) query.archived = params.archived;
    return apiClient.get("/feedback", adminFeedbackListSchema, { query });
  },

  stats: () => apiClient.get("/feedback/stats", feedbackStatsSchema),

  detail: (id: string) => apiClient.get(`/feedback/${id}`, feedbackDetailSchema),

  attachmentUrl: (id: string, attachmentId: string) =>
    apiClient.get(`/feedback/${id}/attachments/${attachmentId}/url`, attachmentUrlSchema),

  patch: (
    id: string,
    body: { status?: FeedbackStatus; category?: FeedbackCategory; archived?: boolean },
  ) => apiClient.patch(`/feedback/${id}`, feedbackDetailSchema, body),

  retryKnowledgeAnalysis: (id: string) =>
    apiClient.post(`/feedback/${id}/knowledge-analysis/retry`, feedbackKnowledgeRetrySchema, {}),

  retryResolution: (id: string) =>
    apiClient.post(`/feedback/${id}/resolution/retry`, feedbackResolutionRetrySchema, {}),

  shortcutPause: (id: string) =>
    apiClient.post(`/feedback/${id}/shortcut/pause`, feedbackDetailSchema, {}),

  shortcutResume: (id: string) =>
    apiClient.post(`/feedback/${id}/shortcut/resume`, feedbackDetailSchema, {}),

  shortcutAcknowledge: (id: string) =>
    apiClient.post(`/feedback/${id}/shortcut/acknowledge`, feedbackDetailSchema, {}),

  jiraPause: (id: string) => apiClient.post(`/feedback/${id}/jira/pause`, feedbackDetailSchema, {}),

  jiraResume: (id: string) =>
    apiClient.post(`/feedback/${id}/jira/resume`, feedbackDetailSchema, {}),

  jiraAcknowledge: (id: string) =>
    apiClient.post(`/feedback/${id}/jira/acknowledge`, feedbackDetailSchema, {}),

  delete: (id: string) => apiClient.delete(`/feedback/${id}`, VOID),
};
